import { EnvironmentProviders, makeEnvironmentProviders } from '@angular/core';
import { MessageService } from 'primeng/api';
import { providePrimeNG } from 'primeng/config';
import { definePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';

const tangiblePreset = definePreset(Aura, {
  // Custom tokens must live under `extend`: as of @primeuix/themes v3 the
  // `primitive` and `semantic` sections only accept the preset's own tokens.
  extend: {
    amethyst: {
      50: '#f7f5fa',
      100: '#d7d0e5',
      200: '#b7aad0',
      300: '#9785bb',
      400: '#785fa7',
      500: '#583a92',
      600: '#4b317c',
      700: '#3e2966',
      800: '#302050',
      900: '#23173a',
      950: '#160f25',
    },
    // Dark-mode primary. Generated with palette('#9f88cd'); light mode keeps amethyst.
    amethystDark: {
      50: '#faf9fd',
      100: '#e8e2f3',
      200: '#d6ccea',
      300: '#c3b5e0',
      400: '#b19fd7',
      500: '#9f88cd',
      600: '#8774ae',
      700: '#6f5f90',
      800: '#574b71',
      900: '#403652',
      950: '#282233',
    },
    colorScheme: {
      light: {
        surface: {
          switch0: '{surface.0}',
          switch50: '{surface.50}',
          switch100: '{surface.100}',
          switch200: '{surface.200}',
          switch300: '{surface.300}',
          switch400: '{surface.400}',
          switch500: '{surface.500}',
          switch600: '{surface.600}',
          switch700: '{surface.700}',
          switch800: '{surface.800}',
          switch900: '{surface.900}',
          switch950: '{surface.950}',
        },
      },
      dark: {
        surface: {
          switch0: '{surface.950}',
          switch50: '{surface.900}',
          switch100: '{surface.800}',
          switch200: '{surface.700}',
          switch300: '{surface.600}',
          switch400: '{surface.500}',
          switch500: '{surface.400}',
          switch600: '{surface.300}',
          switch700: '{surface.200}',
          switch800: '{surface.100}',
          switch900: '{surface.50}',
          switch950: '{surface.0}',
        },
      },
    },
  },
  semantic: {
    colorScheme: {
      light: {
        primary: {
          50: '{amethyst.50}',
          100: '{amethyst.100}',
          200: '{amethyst.200}',
          300: '{amethyst.300}',
          400: '{amethyst.400}',
          500: '{amethyst.500}',
          600: '{amethyst.600}',
          700: '{amethyst.700}',
          800: '{amethyst.800}',
          900: '{amethyst.900}',
          950: '{amethyst.950}',

          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
        },
        surface: {
          0: '#ffffff',
          50: '{slate.50}',
          100: '{slate.100}',
          200: '{slate.200}',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}',
        },
      },
      dark: {
        primary: {
          50: '{amethystDark.950}',
          100: '{amethystDark.900}',
          200: '{amethystDark.800}',
          300: '{amethystDark.700}',
          400: '{amethystDark.600}',
          500: '{amethystDark.500}',
          600: '{amethystDark.400}',
          700: '{amethystDark.300}',
          800: '{amethystDark.200}',
          900: '{amethystDark.100}',
          950: '{amethystDark.50}',

          // Reversed so a higher `primary.*` step is lighter, which keeps
          // bg-primary-50 / hover:bg-primary-100 as dark tints. primary.500
          // is #9f88cd. White on that fill is 3.05:1, so labels use surface.900.
          color: '{primary.500}',
          contrastColor: '{surface.900}',
          hoverColor: '{primary.600}',
          activeColor: '{primary.700}',
        },

        surface: {
          0: '#ffffff',
          50: '{slate.50}',
          100: '{slate.100}',
          200: '{slate.200}',
          300: '{slate.300}',
          400: '{slate.400}',
          500: '{slate.500}',
          600: '{slate.600}',
          700: '{slate.700}',
          800: '{slate.800}',
          900: '{slate.900}',
          950: '{slate.950}',
        },
      },
    },
  },
  components: {
    button: {
      colorScheme: {
        light: {
          root: {
            secondary: {
              background: '#ffffff',
              hoverBackground: '{amethyst.50}',
              activeBackground: '{amethyst.200}',
              borderColor: '{amethyst.100}',
              hoverBorderColor: '{amethyst.200}',
              activeBorderColor: '{amethyst.200}',
              color: '{amethyst.700}',
              hoverColor: '{amethyst.800}',
              activeColor: '{amethyst.800}',
              focusRing: {
                color: '{amethyst.500}',
                shadow: 'none',
              },
            },
          },
          outlined: {
            secondary: {
              hoverBackground: '{amethyst.100}',
              activeBackground: '{amethyst.200}',
              borderColor: '{amethyst.500}',
              color: '{amethyst.500}',
            },
          },
          text: {
            secondary: {
              hoverBackground: '{amethyst.100}',
              activeBackground: '{amethyst.200}',
              color: '{amethyst.500}',
            },
          },
        },
        dark: {
          root: {
            secondary: {
              background: '{amethystDark.900}',
              hoverBackground: '{amethystDark.800}',
              activeBackground: '{amethystDark.700}',
              // The tinted background sits close to the page, so the border
              // carries the 3:1 boundary on its own.
              borderColor: '{amethystDark.400}',
              hoverBorderColor: '{amethystDark.300}',
              activeBorderColor: '{amethystDark.200}',
              color: '{amethystDark.200}',
              hoverColor: '{amethystDark.100}',
              activeColor: '{amethystDark.50}',
              focusRing: {
                color: '{amethystDark.300}',
                shadow: 'none',
              },
            },
          },
          outlined: {
            secondary: {
              hoverBackground:
                'color-mix(in srgb, {amethystDark.300}, transparent 88%)',
              activeBackground:
                'color-mix(in srgb, {amethystDark.300}, transparent 76%)',
              borderColor: '{amethystDark.400}',
              color: '{amethystDark.300}',
            },
          },
          text: {
            secondary: {
              hoverBackground:
                'color-mix(in srgb, {amethystDark.300}, transparent 88%)',
              activeBackground:
                'color-mix(in srgb, {amethystDark.300}, transparent 76%)',
              color: '{amethystDark.300}',
            },
          },
        },
      },
    },
  },
});

export function provideTangiblePrimeNG(): EnvironmentProviders {
  return makeEnvironmentProviders([
    providePrimeNG({
      theme: {
        preset: tangiblePreset,
      },
    }),
    MessageService,
  ]);
}
